import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { Button, Spinner } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useStaffContext } from "../shell/useStaffContext";
import { createFirstVenue } from "./createFirstVenue";

/**
 * The venue picker: the landing point when no venue is in view, an unknown/unassigned `/v/:id` is
 * hit, or a guard sends the user to switch. Lists the assigned venues from the bootstrap query.
 */
export function SelectVenue() {
  const { venues, isLoading, isError, claims } = useStaffContext();
  const { t } = useTranslation();

  return (
    <main
      className={css({
        p: "8",
        display: "flex",
        flexDirection: "column",
        gap: "4",
        maxW: "xl",
        mx: "auto",
      })}
    >
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold" })}>{t("shell.picker.title")}</h1>
      {isLoading ? (
        <Spinner aria-label={t("shell.loading")} />
      ) : isError ? (
        <p role="alert" className={css({ color: "fg.muted" })}>
          {t("shell.bootstrapError")}
        </p>
      ) : venues.length === 0 ? (
        // An ACCOUNT ROOT with no venue can fix that themselves; a waiter cannot, and telling them
        // to create a restaurant would be worse than the bare message. Signup creates the first
        // venue for new owners, so this covers the accounts it cannot reach: those that predate
        // that flow, one whose only venue was offboarded, or a signup whose second half failed.
        claims.isAccountRoot ? (
          <CreateFirstVenue />
        ) : (
          <p className={css({ color: "fg.muted" })}>{t("shell.picker.empty")}</p>
        )
      ) : (
        <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          {venues.map((v) => (
            <li key={v.id}>
              <Link
                to={`/v/${v.id}`}
                className={css({
                  display: "block",
                  px: "4",
                  py: "3",
                  borderWidth: "1px",
                  borderColor: "border.default",
                  rounded: "md",
                  _hover: { bg: "bg.subtle" },
                })}
              >
                <span className={css({ fontWeight: "semibold" })}>{v.displayName}</span>
                <span className={css({ color: "fg.muted", fontSize: "sm", ml: "2" })}>
                  {v.timeZone}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

/** The empty-state form: name the restaurant, create it, and go straight into onboarding. */
function CreateFirstVenue() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || pending) return;
    setPending(true);
    setFailed(false);

    const venueId = await createFirstVenue(trimmed, queryClient);
    if (venueId === null) {
      setFailed(true);
      setPending(false);
      return;
    }
    navigate(`/v/${venueId}/onboarding`, { replace: true });
  };

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
      <p className={css({ color: "fg.muted" })}>{t("picker.firstVenue.subtitle")}</p>
      <form onSubmit={submit} className={css({ display: "grid", gap: "3", maxW: "md" })}>
        <label htmlFor="venueName" className={css({ fontSize: "sm", fontWeight: "medium" })}>
          {t("picker.firstVenue.label")}
        </label>
        <input
          id="venueName"
          name="venueName"
          type="text"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          placeholder={t("picker.firstVenue.placeholder")}
          autoComplete="organization"
          required
          disabled={pending}
          aria-invalid={failed || undefined}
          aria-describedby={failed ? "venue-error" : undefined}
          className={css({
            px: "3",
            py: "2",
            minH: "44px",
            rounded: "md",
            borderWidth: "1px",
            borderColor: "border.default",
            bg: "bg.default",
            fontSize: "md",
          })}
        />
        {failed ? (
          <p id="venue-error" role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
            {t("picker.firstVenue.error")}
          </p>
        ) : null}
        <Button type="submit" disabled={pending || name.trim() === ""}>
          {pending ? t("picker.firstVenue.submitting") : t("picker.firstVenue.submit")}
        </Button>
      </form>
    </div>
  );
}
